import type { Task } from "@myapp/contract";
import { CompleteTaskModal, EndTaskDepartureModal } from "../../../features/work-session";
import { SuccessDialog } from "../../../shared/ui/feedback/SuccessDialog/SuccessDialog";
import { WorkerTaskPlanOverlay } from "../../../widgets/worker-task-plan/ui/WorkerTaskPlanOverlay";
import type { TaskFlows } from "../model/useTaskFlows";

type Props = {
  task: Task;
  technicianId: number;
  flows: TaskFlows;
};

/**
 * Every modal, overlay and success dialog the task page can open, driven entirely by `flows` — the
 * counterpart of `AppointmentFlowDialogs`. Rendered as a sibling of `PageLayout`, not inside it —
 * `Modal` positions itself `fixed` rather than through a portal, so nesting it inside a scrolling
 * container would clip it.
 */
export const TaskFlowDialogs = ({ task, technicianId, flows }: Props) => {
  const { flow, close, onDepartureSaved, onCompleted, onPlanned, success } = flows;
  const setShow = (open: boolean) => { if (!open) close(); };

  return (
    <>
      <EndTaskDepartureModal
        show={flow === "end-departure"}
        setShow={setShow}
        taskId={task.id}
        actualEndsAt={task.schedule?.actualEndsAt ?? null}
        onSuccess={onDepartureSaved}
      />

      {/* Guarded on `task.schedule`, not just `show`: `CompleteTaskModal`'s own effect reads
          `schedule.actualWorkStartsAt` unconditionally in its dependency array, so mounting it at
          all for an unscheduled task throws on render — before `show` is ever checked. An
          unscheduled task can never actually reach "complete" (`getPrimaryWorkAction` returns
          null while unscheduled), so this only guards against the crash, not a real flow. */}
      {task.schedule && (
        <CompleteTaskModal
          show={flow === "complete"}
          setShow={setShow}
          taskId={task.id}
          schedule={task.schedule}
          technicianComment={task.technicianComment}
          onSuccess={onCompleted}
        />
      )}

      <SuccessDialog
        open={success.open}
        onOpenChange={(open) => { if (!open) success.onClose(); }}
        title={success.title}
        onClose={success.onClose}
      />

      {/* Mounted only while open — `CalendarPlanningOverlay` fetches the technician's schedule
          before its own `show` check, so an always-mounted overlay would fire that on every page
          load whether or not the worker ever opens it. */}
      {flow === "plan" && (
        <WorkerTaskPlanOverlay
          show
          onClose={close}
          onSuccess={onPlanned}
          task={task}
          technicianId={technicianId}
        />
      )}
    </>
  );
};
